import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppBar from "../../../components/atoms/appbar";
import Button from "../../../components/atoms/button";
import SelectBox from "../../../components/atoms/select";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore";
import { useAccessStore } from "../../../lib/store/useAccessStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  emailUpdate,
  logout,
  updateNickname,
  updateRegion,
} from "../../../@apis/v1/auth.service";
import { useModalStore } from "../../../lib/store/useModalStore";
import { setGlobalAuthHeader } from "../../../@apis/axios.config";
import { resetUserSession } from "../../../@apis/queryClient";
import { getZodiacImg, maskBirth } from "../../../lib/utils/birthdayUtils";
import { maskName } from "../../../lib/utils/maskName";
import { maskPhone } from "../../../lib/utils/maskPhone";
import { vw } from "../../../lib/utils/responsiveUtils";
import { cn } from "../../../lib/utils/cn";
import { useMyInfo } from "../../../@apis/hooks/useAuth";
import { useSidoList, useSigunguList } from "../../../@apis/hooks/useRegion";
import type { UpdateRegionRequest } from "../../../@apis/dto/request/auth.request";
import type { OnErrorResponse } from "../../../@apis/dto/response/common.response";

// 닉네임 변경 실패 에러 코드별 안내 메시지
const NICKNAME_ERROR_MESSAGES: Record<string, string> = {
  S019: "이미 사용 중인 닉네임입니다.\n다른 닉네임을 입력해 주세요.",
  S020: "닉네임은 한글, 영문, 숫자만 사용할 수 있습니다.",
  S021: "부적절한 단어가 포함되어 있습니다.\n다른 닉네임을 입력해 주세요.",
};

export default function SettingInfo() {
  const { access } = useAccessStore();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModalStore();
  const queryClient = useQueryClient();
  // 이메일 수정 모달 id (성공 alert 확인 시 함께 닫기 위해 보관)
  const emailModalIdRef = useRef<string | null>(null);
  // 닉네임 변경 모달 id (성공 alert 확인 시 함께 닫기 위해 보관)
  const nicknameModalIdRef = useRef<string | null>(null);

  const { data: myInfoData } = useQuery({
    ...useMyInfo(),
    enabled: !!access.accessToken,
  });
  const myInfo = myInfoData?.data;
  const zodiacImg = getZodiacImg(myInfo?.birthdate ?? "");

  // 선호 지역 - myInfo 값을 디폴트로 사용
  const [sidoCode, setSidoCode] = useState("");
  const [sigunguCode, setSigunguCode] = useState("");

  useEffect(() => {
    if (!myInfo) return;
    setSidoCode(myInfo.sidoCode ?? "");
    setSigunguCode(myInfo.sigunguCode ?? "");
  }, [myInfo?.sidoCode, myInfo?.sigunguCode]);

  const { data: sidoData } = useQuery({ ...useSidoList() });
  const { data: sigunguData, isFetching: isSigunguFetching } = useQuery({
    ...useSigunguList(sidoCode),
  });

  const sidoOptions = useMemo(
    () => [
      { label: "시/도 선택", value: "" },
      ...(sidoData?.data ?? []).map(({ code, name }) => ({
        label: name,
        value: code,
      })),
    ],
    [sidoData?.data],
  );

  const sigunguOptions = useMemo(
    () => [
      {
        label: isSigunguFetching ? "불러오는 중" : "시/군/구 선택",
        value: "",
      },
      ...(sigunguData?.data ?? []).map(({ code, name }) => ({
        label: name,
        value: code,
      })),
    ],
    [isSigunguFetching, sigunguData?.data],
  );

  const updateRegionMutation = useMutation({
    mutationFn: (form: UpdateRegionRequest) => updateRegion(form),
    onSuccess: (_res, variables) => {
      // 저장 성공 시 store 및 서버 쿼리 갱신
      useMyInfoStore.getState().setMyInfo({
        sidoCode: variables.sidoCode,
        sidoName: variables.sidoName,
        sigunguCode: variables.sigunguCode,
        sigunguName: variables.sigunguName,
      });
      queryClient.invalidateQueries({ queryKey: ["auth-my-info"] });
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      openModal("ALERT_DIALOG", {
        title: "알림",
        content: error?.message ?? "선호 지역 저장에 실패했습니다.",
      });
    },
  });

  const updateNicknameMutation = useMutation({
    mutationFn: (nickname: string) => updateNickname(nickname),
    onSuccess: (res) => {
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: ["auth-my-info"] });
        openModal("ALERT_DIALOG", {
          title: "닉네임 변경 성공",
          content: "닉네임이 변경되었습니다.",
          // 확인 클릭 시 닉네임 변경 모달도 함께 닫기
          onConfirm: () => {
            if (nicknameModalIdRef.current) {
              closeModal(nicknameModalIdRef.current);
              nicknameModalIdRef.current = null;
            }
          },
        });
      }
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      openModal("ALERT_DIALOG", {
        title: "닉네임 변경 실패",
        content:
          NICKNAME_ERROR_MESSAGES[error?.code ?? ""] ?? error?.message,
      });
    },
  });

  const updateEmailMutation = useMutation({
    mutationFn: (email: string) => emailUpdate(email),
    onSuccess: (res) => {
      const { success } = res;
      if (success) {
        queryClient.invalidateQueries({ queryKey: ["auth-my-info"] });

        openModal("ALERT_DIALOG", {
          title: "이메일 수정 완료",
          content: "이메일이 변경되었습니다.",
          // 확인 클릭 시 이메일 수정 모달도 함께 닫기
          onConfirm: () => {
            if (emailModalIdRef.current) {
              closeModal(emailModalIdRef.current);
              emailModalIdRef.current = null;
            }
          },
        });
      }
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      openModal("ALERT_DIALOG", {
        title: "이메일 수정 실패",
        content: error?.message ?? "올바른 이메일 형식으로 입력해 주세요.",
      });
    },
  });

  const handleSidoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCode = e.target.value;
    const selectedName =
      sidoData?.data.find(({ code }) => code === selectedCode)?.name ?? "";

    setSidoCode(selectedCode);
    // 시/도 변경 시 시/군/구 초기화
    setSigunguCode("");
    updateRegionMutation.mutate({
      sidoCode: selectedCode,
      sidoName: selectedName,
      sigunguCode: "",
      sigunguName: "",
    });
  };

  const handleSigunguChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCode = e.target.value;
    const selectedName =
      sigunguData?.data.find(({ code }) => code === selectedCode)?.name ?? "";
    const selectedSidoName =
      sidoData?.data.find(({ code }) => code === sidoCode)?.name ?? "";

    setSigunguCode(selectedCode);
    updateRegionMutation.mutate({
      sidoCode,
      sidoName: selectedSidoName,
      sigunguCode: selectedCode,
      sigunguName: selectedName,
    });
  };

  const logoutMutation = useMutation({
    mutationFn: () => logout(access.refreshToken),
    onSuccess: (res) => {
      const { success } = res;
      if (success) {
        useAccessStore.getState().clearAccess();
        resetUserSession();
        navigate("/login", { replace: true });
        setGlobalAuthHeader("");
      } else {
        openModal("ALERT_DIALOG", {
          title: "에러",
          content: "로그아웃에 실패하였습니다.",
        });
      }
    },
    onError: () =>
      openModal("ALERT_DIALOG", {
        title: "에러",
        content: "로그아웃에 실패하였습니다.",
      }),
  });

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <AppBar backButton title="내 정보" />
      <section className="px-[20px] *:border-b *:border-[#e5e5ea]">
        {/* 프로필 */}
        <div className="py-[20px] gap-x-[8px] flex items-center">
          {/* 프로필 사진 */}
          <div className="px-[8px] py-[4px]">
            <div className="rounded-full bg-[#f5f5f5] size-[49px] shrink-0">
              <img src={zodiacImg} alt="띠 이미지" />
            </div>
          </div>

          {/* 닉네임 */}
          <p className="text-2xl truncate font-medium flex-1">
            {myInfo?.nickName}
          </p>

          <Button
            className="rounded-full shrink-0 px-[8px] py-[2px]  border-[#8e8e93] cursor-pointer"
            variant={"white"}
            onClick={() => {
              nicknameModalIdRef.current = openModal("NICKNAME_DIALOG", {
                defaultValue: myInfo?.nickName ?? "",
                onConfirm: (nickname: string) => {
                  // 기존 닉네임과 동일하면 API 호출하지 않음
                  if (nickname === (myInfo?.nickName ?? "")) {
                    openModal("ALERT_DIALOG", {
                      title: "알림",
                      content: "기존 닉네임과 동일합니다.",
                    });
                    return;
                  }
                  updateNicknameMutation.mutate(nickname);
                },
              });
            }}
          >
            <div className="flex gap-x-[8px] text-[#8e8e93] text-sm">
              <img
                className="size-[20px]"
                alt="닉네임 변경"
                src="/icons/ic-pencil-edit.png"
                role="button"
              />
              닉네임 변경
            </div>
          </Button>
        </div>
        {/* 내 정보 */}
        <div
          className="py-[20px] space-y-[20px]"
          onClick={() => navigate("/setting/info/edit")}
        >
          <div className="space-y-[12px]">
            {/* 이름 */}
            <p className="flex items-center gap-x-[12px] *:text-base *:font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">이름</span>
              <span>{maskName(myInfo?.name)}</span>
            </p>
            {/* 휴대폰 */}
            <p className="flex items-center gap-x-[12px] *:text-base *:font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">휴대폰</span>
              <span>{maskPhone(myInfo?.mobileno)}</span>
            </p>
            {/* 생년월일 */}
            <p className="flex items-center gap-x-[12px] *:text-base *:font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">생년월일</span>
              <span>{maskBirth(myInfo?.birthdate)}</span>
            </p>
            {/* 성별 */}
            <p className="flex items-center gap-x-[12px] *:text-base *:font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">성별</span>
              <span>{myInfo?.gender == "0" ? "여성" : "남성"}</span>
            </p>
          </div>
          {/* 내 정보 수정 버튼 */}
          <Button
            variant="black"
            className="w-full rounded-[8px] text-[#f5f5f5] font-normal py-[12px] text-base"
          >
            내 정보 수정
          </Button>
        </div>
        {/* 추가 정보 (선택) */}
        <div className="py-[20px] space-y-[16px]">
          <h2 className="text-[#8e8e93] text-sm font-medium">
            추가 정보 (선택)
          </h2>
          {/* 선호 지역 */}
          <div className="space-y-[8px]">
            <p className="text-[#1e1e1e] text-base font-normal">선호 지역</p>
            <div className="flex items-center gap-x-[16px]">
              {/* 시/도 선택 */}
              <SelectBox
                options={sidoOptions}
                value={sidoCode}
                onChange={handleSidoChange}
                className={cn(
                  "text-[0.7rem] placeholder:text-[0.7rem] w-full",
                  sidoCode === "" ? "text-[#888]" : "",
                )}
              />
              {/* 시/군/구 선택 */}
              <SelectBox
                options={sigunguOptions}
                value={sigunguCode}
                onChange={handleSigunguChange}
                disabled={!sidoCode || isSigunguFetching}
                className={cn(
                  "text-[0.7rem] placeholder:text-[0.7rem] w-full",
                  sigunguCode === "" ? "text-[#888]" : "",
                )}
              />
            </div>
          </div>
        </div>

        <div
          className="py-[16px] flex justify-between items-center gap-x-[4px] min-w-0"
          onClick={() => {
            emailModalIdRef.current = openModal("EMAIL_DIALOG", {
              defaultValue: myInfo?.email ?? "",
              onConfirm: (email: string) => {
                // 기존 이메일과 동일하면 API 호출하지 않음
                if (email === (myInfo?.email ?? "")) {
                  openModal("ALERT_DIALOG", {
                    title: "알림",
                    content: "기존 이메일과 동일합니다.",
                  });
                  return;
                }
                updateEmailMutation.mutate(email);
              },
            });
          }}
        >
          <p className="flex items-start gap-x-[12px] min-w-0 flex-1">
            <span className="text-base text-[#8e8e93] font-medium min-w-[4.4rem] shrink-0">
              이메일
            </span>
            <span className="min-w-0 break-all">{myInfo?.email || "-"}</span>
          </p>

          <img
            className="mr-[6px] shrink-0"
            style={{ width: vw(7), height: vw(12) }}
            src="/icons/ic-go.webp"
          />
        </div>
        {/* <div className="flex-row-between border-t border-[#e6e6e6] py-[24px]">
          <p className="font-[500] text-[#222]" style={{ fontSize: vw(14) }}>앱 푸시 알림</p>
          <img className="w-[6px] h-[14px]" src="/icons/ic-go.webp" />
        </div> */}
      </section>
      <div className="absolute bottom-0 left-0 p-[20px] space-x-[12px]">
        <button
          className="text-[#8e8e93] text-sm  border-b border-[#8e8e93] leading-none"
          onClick={() =>
            openModal("CONFIRM_DIALOG", {
              title: "로그아웃",
              content: "로그아웃 하시겠습니까?",
              onClick: () => logoutMutation.mutate(),
            })
          }
        >
          로그아웃
        </button>

        <button
          className="text-[#8e8e93] text-sm  border-b border-[#8e8e93] leading-none"
          onClick={() => navigate("/withdraw")}
        >
          회원탈퇴
        </button>
      </div>
    </div>
  );
}
